import React, { useEffect, useState } from 'react';
import { CreditCard, CheckCircle, MessageSquare, PhoneCall, ExternalLink, Loader, Plus, Minus } from 'lucide-react';
import '../../styles/designSystem.css';
import {
  getCurrentSubscription,
  formatValidityDate,
  calculateDaysRemaining,
  Subscription,
  createPaymentOrder,
  initializeRazorpayPayment,
  CreatePaymentOrderPayload
} from '../../services/subscription';
import toast from 'react-hot-toast';

const SubscriptionSettings: React.FC = () => {
  const [planType, setPlanType] = useState('quarterly');
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [userQuantity, setUserQuantity] = useState(5); // Default to 5 users

  // Plan IDs from backend
  const PREMIUM_QUARTERLY_PLAN_ID = '64468a1113bdc104cec11179';
  const PREMIUM_YEARLY_PLAN_ID = '64468a4213bdc104cec1117a';
  const ULTIMATE_QUARTERLY_PLAN_ID = '658050be5f1d953b2780d0fc';
  const ULTIMATE_YEARLY_PLAN_ID = '65805a165f1d953b2780d0ff';

  // Premium+ pricing (per user)
  const premiumQuarterlyPrice = 1000;
  const premiumYearlyPrice = 2000; // 50% savings

  // Ultimate pricing (per user)
  const ultimateQuarterlyPrice = 1500;
  const ultimateYearlyPrice = 3000; // 50% savings

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await getCurrentSubscription();
      const subscription = response.data.data;
      setCurrentSubscription(subscription);

      // Initialize quantity from current subscription or keep default (5)
      if (subscription && subscription.quantity && subscription.quantity > 0) {
        setUserQuantity(subscription.quantity);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  // Quantity handlers
  const incrementQuantity = () => {
    setUserQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    setUserQuantity(prev => Math.max(1, prev - 1)); // Minimum 1 user
  };

  // Calculate total price based on plan and quantity
  const calculateTotalPrice = (planName: string): number => {
    let pricePerUser = 0;

    if (planName.toLowerCase().includes('premium')) {
      pricePerUser = planType === 'quarterly' ? premiumQuarterlyPrice : premiumYearlyPrice;
    } else if (planName.toLowerCase().includes('ultimate')) {
      pricePerUser = planType === 'quarterly' ? ultimateQuarterlyPrice : ultimateYearlyPrice;
    }

    return pricePerUser * userQuantity;
  };

  // Handle subscription purchase
  const handlePurchase = async (planId: string, planName: string) => {
    if (!planId) {
      toast.error('Please select a plan');
      return;
    }

    setPurchasing(true);
    try {
      // Create payment order
      const payload: CreatePaymentOrderPayload = {
        plan: planId,
        quantity: userQuantity
      };

      const orderResponse = await createPaymentOrder(payload);
      const paymentOrder = orderResponse.data.data; // Nested data structure

      // Get user details for Razorpay
      const userEmail = localStorage.getItem('user_email') || '';
      const userName = localStorage.getItem('user_name') || '';
      const userPhone = localStorage.getItem('user_phone') || '';

      // Initialize Razorpay payment
      await initializeRazorpayPayment(
        paymentOrder,
        {
          email: userEmail,
          name: userName,
          phone: userPhone
        },
        {
          onSuccess: async (response) => {
            toast.success('Payment successful! Your subscription is now active.');
            await fetchSubscription(); // Refresh subscription data
          },
          onError: (error) => {
            toast.error(`Payment failed: ${error.description || 'Please try again'}`);
          },
          onDismiss: () => {
            setPurchasing(false);
          }
        }
      );
    } catch (error: any) {
      console.error('Error creating payment order:', error);
      toast.error(error?.response?.data?.message || 'Failed to initiate payment. Please try again.');
      setPurchasing(false);
    }
  };

  // Get current plan details
  const currentPlanDetails = {
    status: currentSubscription?.subscriptionPlan?.planType || 'free',
    isTrail: currentSubscription?.isTrail || false,
    quantity: currentSubscription?.quantity || 1,
    daysRemaining: currentSubscription ? calculateDaysRemaining(currentSubscription) : 0
  };

  // Check if user has active paid subscription (not trial, not free)
  const hasActivePaidSubscription = currentSubscription &&
    currentSubscription.status !== 'free' &&
    !currentPlanDetails.isTrail &&
    currentPlanDetails.daysRemaining > 0;

  // Check which plan is currently active (exclude trial users - they can select plans)
  const isCurrentPlan = (planName: string) => {
    // If it's a trial, don't mark any plan as current
    if (currentPlanDetails.isTrail) {
      return false;
    }
    return currentPlanDetails.status.toLowerCase() === planName.toLowerCase();
  };

  // Check if a plan is a downgrade (not allowed)
  const isDowngrade = (planName: string) => {
    // Only block downgrades for active paid subscriptions
    if (!hasActivePaidSubscription) {
      return false;
    }

    const currentPlan = currentPlanDetails.status.toLowerCase();
    const targetPlan = planName.toLowerCase();

    // Ultimate users cannot downgrade to Premium
    if (currentPlan === 'ultimate' && targetPlan.includes('premium')) {
      return true;
    }

    return false;
  };

  if (loading) {
    return (
      <div className="settings-content-wrapper">
        <div className="ds-flex ds-items-center ds-justify-center" style={{ minHeight: '20rem' }}>
          <Loader className="animate-spin" size={32} style={{ color: 'var(--color-primary)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="settings-content-wrapper">
      {/* Page Header */}
      <div style={{ marginBottom: 'var(--spacing-8)', textAlign: 'center' }}>
        <h2 className="ds-h2" style={{ margin: '0 0 0.5rem 0' }}>Subscription Plans</h2>
        <p className="ds-helper-text" style={{ margin: 0, maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
          Choose the plan that fits your team size and business needs. All plans include unlimited leads, tasks, and content.
        </p>
      </div>

      {/* Trial Banner - Prominent */}
      {currentSubscription && currentPlanDetails.isTrail && currentPlanDetails.daysRemaining > 0 && (
        <div className="ds-card" style={{
          maxWidth: '70rem',
          margin: '0 auto 3rem auto',
          background: currentPlanDetails.daysRemaining <= 3
            ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          border: 'none',
          padding: 'var(--spacing-8)',
          boxShadow: currentPlanDetails.daysRemaining <= 3
            ? '0 8px 32px rgba(220, 38, 38, 0.4)'
            : '0 8px 32px rgba(245, 158, 11, 0.4)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(255,255,255,0.2)',
              padding: '0.5rem 1.5rem',
              borderRadius: 'var(--radius-full)',
              marginBottom: '1rem'
            }}>
              <CheckCircle size={20} />
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.05em' }}>
                TRIAL ACTIVE
              </span>
            </div>

            <h2 className="ds-h2" style={{
              margin: '0 0 1.5rem 0',
              color: 'white',
              fontSize: 'var(--font-size-5xl)',
              fontWeight: 'var(--font-weight-bold)'
            }}>
              {currentPlanDetails.daysRemaining} {currentPlanDetails.daysRemaining === 1 ? 'Day' : 'Days'} Left in Trial
            </h2>

            <p style={{
              margin: '0',
              fontSize: 'var(--font-size-lg)',
              opacity: 0.95,
              fontWeight: 'var(--font-weight-medium)',
              maxWidth: '500px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              {currentPlanDetails.daysRemaining <= 3
                ? '⚠️ Your trial is expiring soon! Choose a plan below to continue using all features.'
                : 'Choose a plan below to unlock all features and continue after your trial ends.'}
            </p>

            <p style={{
              margin: '0.75rem 0 0 0',
              fontSize: 'var(--font-size-sm)',
              opacity: 0.9
            }}>
              Trial expires on {formatValidityDate(currentSubscription)}
            </p>
          </div>
        </div>
      )}

      {/* Paid Subscription Banner */}
      {currentSubscription && currentSubscription.status !== 'free' && !currentPlanDetails.isTrail && (
        <div className="ds-card" style={{
          maxWidth: '70rem',
          margin: '0 auto 3rem auto',
          background: 'var(--gradient-primary)',
          color: 'white',
          border: 'none',
          padding: 'var(--spacing-8)',
          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(255,255,255,0.2)',
              padding: '0.5rem 1.5rem',
              borderRadius: 'var(--radius-full)',
              marginBottom: '1rem'
            }}>
              <CheckCircle size={20} />
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.05em' }}>
                ACTIVE SUBSCRIPTION
              </span>
            </div>

            <h2 className="ds-h2" style={{
              margin: '0 0 1rem 0',
              color: 'white',
              fontSize: 'var(--font-size-4xl)',
              textTransform: 'capitalize'
            }}>
              {currentPlanDetails.status} Plan
            </h2>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 'var(--spacing-8)',
              flexWrap: 'wrap',
              marginBottom: '0.5rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 'var(--font-size-3xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'white'
                }}>
                  {currentPlanDetails.quantity}
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  Licensed Users
                </div>
              </div>

              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)' }}></div>

              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 'var(--font-size-3xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'white'
                }}>
                  {currentPlanDetails.daysRemaining}
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', opacity: 0.9 }}>
                  Days Remaining
                </div>
              </div>
            </div>

            <p style={{
              margin: '1rem 0 0 0',
              fontSize: 'var(--font-size-sm)',
              opacity: 0.9,
              fontWeight: 'var(--font-weight-medium)'
            }}>
              Valid until {formatValidityDate(currentSubscription)}
            </p>
          </div>
        </div>
      )}

      {/* Plan Toggle */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{
          display: 'inline-flex',
          background: 'var(--color-neutral-100)',
          borderRadius: '9999px',
          padding: '0.25rem',
          border: '1px solid var(--border-light)'
        }}>
          <button
            className={`ds-btn ${planType === 'quarterly' ? 'ds-btn-primary' : 'ds-btn-ghost'}`}
            onClick={() => setPlanType('quarterly')}
            style={{ borderRadius: '9999px', padding: '0.5rem 1.5rem' }}
          >
            Quarterly
          </button>
          <button
            className={`ds-btn ${planType === 'yearly' ? 'ds-btn-primary' : 'ds-btn-ghost'}`}
            onClick={() => setPlanType('yearly')}
            style={{ borderRadius: '9999px', padding: '0.5rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Yearly
            <span className="ds-badge ds-badge-success" style={{ padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}>
              Save 50%
            </span>
          </button>
        </div>
      </div>

      {/* Shared Quantity Selector - Only show if no active paid subscription */}
      {!hasActivePaidSubscription && (
        <div className="ds-card" style={{
          maxWidth: '500px',
          margin: '0 auto 2rem auto',
          padding: 'var(--spacing-6)',
          background: 'var(--color-neutral-50)',
          border: '2px solid var(--color-primary)'
        }}>
          <label className="ds-label" style={{
            display: 'block',
            textAlign: 'center',
            marginBottom: '1rem',
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-primary)'
          }}>
            Number of Users
          </label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1.5rem'
          }}>
            <button
              className="ds-btn ds-btn-secondary ds-btn-lg"
              onClick={decrementQuantity}
              style={{
                width: '3rem',
                height: '3rem',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              type="button"
            >
              <Minus size={20} />
            </button>
            <span style={{
              fontSize: 'var(--font-size-4xl)',
              fontWeight: 'var(--font-weight-bold)',
              minWidth: '4rem',
              textAlign: 'center',
              color: 'var(--color-primary)'
            }}>
              {userQuantity}
            </span>
            <button
              className="ds-btn ds-btn-secondary ds-btn-lg"
              onClick={incrementQuantity}
              style={{
                width: '3rem',
                height: '3rem',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              type="button"
            >
              <Plus size={20} />
            </button>
          </div>
          <p className="ds-body-sm ds-text-tertiary" style={{ textAlign: 'center', margin: '0.75rem 0 0 0' }}>
            Adjust the number of users to see pricing for each plan below
          </p>
        </div>
      )}

      {/* Pricing Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '2rem',
        maxWidth: '60rem',
        margin: '0 auto'
      }}>
        {/* Premium+ Plan */}
        <div className="ds-card" style={{
          position: 'relative',
          overflow: 'visible',
          border: isCurrentPlan('premium+') ? '2px solid var(--color-success)' : undefined,
          background: isCurrentPlan('premium+') ? 'rgba(34, 197, 94, 0.05)' : undefined
        }}>
          {isCurrentPlan('premium+') && (
            <div style={{
              position: 'absolute',
              top: '-0.75rem',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--color-success)',
              color: 'white',
              padding: '0.25rem 1rem',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <CheckCircle size={16} />
              Your Current Plan
            </div>
          )}

          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <h3 className="ds-h3" style={{ margin: '0 0 0.5rem 0', color: 'var(--color-primary)' }}>Premium+</h3>
            <p className="ds-body-sm ds-text-secondary">Perfect for growing businesses</p>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
              <span className="ds-h1" style={{ fontSize: 'var(--font-size-5xl)', color: 'var(--color-primary)' }}>
                ₹{planType === 'quarterly' ? premiumQuarterlyPrice : premiumYearlyPrice}
              </span>
              <span className="ds-body-sm ds-text-tertiary">/user/{planType === 'quarterly' ? '3 months' : 'year'}</span>
            </div>
            {planType === 'yearly' && (
              <p className="ds-body-sm ds-text-tertiary" style={{ margin: '0.5rem 0 0 0' }}>
                <span style={{ textDecoration: 'line-through' }}>₹4000</span> Save 50%
              </p>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            {[
              'Unlimited Leads',
              'Auto-sync 20+ sources',
              'Lead forms & bulk import',
              'Google Sheets & Contacts sync',
              'Dashboard & reports',
              'WhatsApp support (24-48 hr SLA)'
            ].map((feature, idx) => (
              <div key={idx} className="ds-flex ds-items-center ds-gap-3" style={{ marginBottom: '0.75rem' }}>
                <CheckCircle size={20} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                <span className="ds-body-sm">{feature}</span>
              </div>
            ))}
          </div>

          {/* Total Price Display - Hide if downgrade */}
          {!isCurrentPlan('premium+') && !isDowngrade('premium+') && (
            <div style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: 'var(--spacing-4)',
              background: 'var(--gradient-primary)',
              borderRadius: 'var(--radius-lg)',
              color: 'white'
            }}>
              <div style={{ fontSize: 'var(--font-size-sm)', marginBottom: '0.25rem', opacity: 0.9 }}>
                Total for {userQuantity} {userQuantity === 1 ? 'user' : 'users'}
              </div>
              <div style={{
                fontSize: 'var(--font-size-4xl)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
                ₹{calculateTotalPrice('premium+').toLocaleString()}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.9, marginTop: '0.25rem' }}>
                ₹{planType === 'quarterly' ? premiumQuarterlyPrice : premiumYearlyPrice}/user × {userQuantity}
              </div>
            </div>
          )}

          {/* Downgrade warning message */}
          {isDowngrade('premium+') && (
            <div style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: 'var(--spacing-4)',
              background: 'var(--color-neutral-100)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--color-text-tertiary)'
            }}>
              <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                ⓘ Downgrades are not available. Contact support if you need to change your plan.
              </p>
            </div>
          )}

          <button
            className="ds-btn ds-btn-primary"
            style={{ width: '100%' }}
            onClick={() => handlePurchase(
              planType === 'quarterly' ? PREMIUM_QUARTERLY_PLAN_ID : PREMIUM_YEARLY_PLAN_ID,
              'Premium+'
            )}
            disabled={isCurrentPlan('premium+') || purchasing || isDowngrade('premium+')}
          >
            {purchasing ? (
              <>
                <Loader className="animate-spin" size={16} />
                Processing...
              </>
            ) : isCurrentPlan('premium+') ? (
              'Current Plan'
            ) : isDowngrade('premium+') ? (
              'Downgrade Not Available'
            ) : (
              'Buy Subscription'
            )}
          </button>
        </div>

        {/* Ultimate Plan */}
        <div className="ds-card" style={{
          position: 'relative',
          border: isCurrentPlan('ultimate') ? '2px solid var(--color-success)' : '2px solid var(--color-primary)',
          overflow: 'visible',
          background: isCurrentPlan('ultimate') ? 'rgba(34, 197, 94, 0.05)' : undefined
        }}>
          {isCurrentPlan('ultimate') ? (
            <div style={{
              position: 'absolute',
              top: '-0.75rem',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--color-success)',
              color: 'white',
              padding: '0.25rem 1rem',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              <CheckCircle size={16} />
              Your Current Plan
            </div>
          ) : (
            <div style={{
              position: 'absolute',
              top: '-0.75rem',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '0.25rem 1rem',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
            }}>
              ⭐ Best Value
            </div>
          )}

          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <h3 className="ds-h3" style={{ margin: '0 0 0.5rem 0', color: 'var(--color-primary)' }}>Ultimate</h3>
            <p className="ds-body-sm ds-text-secondary">For power users & enterprises</p>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
              <span className="ds-h1" style={{ fontSize: 'var(--font-size-5xl)', color: 'var(--color-primary)' }}>
                ₹{planType === 'quarterly' ? ultimateQuarterlyPrice : ultimateYearlyPrice}
              </span>
              <span className="ds-body-sm ds-text-tertiary">/user/{planType === 'quarterly' ? '3 months' : 'year'}</span>
            </div>
            {planType === 'yearly' && (
              <p className="ds-body-sm ds-text-tertiary" style={{ margin: '0.5rem 0 0 0' }}>
                <span style={{ textDecoration: 'line-through' }}>₹6000</span> Save 50%
              </p>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            {[
              'Everything in Premium+',
              'WhatsApp API integration',
              'Email & WhatsApp campaigns',
              'Orders, invoices & quotes',
              'Advanced automations',
              'iOS app',
              'Priority support (4-8 hr SLA)'
            ].map((feature, idx) => (
              <div key={idx} className="ds-flex ds-items-center ds-gap-3" style={{ marginBottom: '0.75rem' }}>
                <CheckCircle size={20} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                <span className="ds-body-sm">{feature}</span>
              </div>
            ))}
          </div>

          {/* Total Price Display */}
          {!isCurrentPlan('ultimate') && (
            <div style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: 'var(--spacing-4)',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: 'var(--radius-lg)',
              color: 'white',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
            }}>
              <div style={{ fontSize: 'var(--font-size-sm)', marginBottom: '0.25rem', opacity: 0.9 }}>
                Total for {userQuantity} {userQuantity === 1 ? 'user' : 'users'}
              </div>
              <div style={{
                fontSize: 'var(--font-size-4xl)',
                fontWeight: 'var(--font-weight-bold)'
              }}>
                ₹{calculateTotalPrice('ultimate').toLocaleString()}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.9, marginTop: '0.25rem' }}>
                ₹{planType === 'quarterly' ? ultimateQuarterlyPrice : ultimateYearlyPrice}/user × {userQuantity}
              </div>
            </div>
          )}

          <button
            className="ds-btn ds-btn-primary"
            style={{ width: '100%' }}
            onClick={() => handlePurchase(
              planType === 'quarterly' ? ULTIMATE_QUARTERLY_PLAN_ID : ULTIMATE_YEARLY_PLAN_ID,
              'Ultimate'
            )}
            disabled={isCurrentPlan('ultimate') || purchasing}
          >
            {purchasing ? (
              <>
                <Loader className="animate-spin" size={16} />
                Processing...
              </>
            ) : isCurrentPlan('ultimate') ? (
              'Current Plan'
            ) : currentPlanDetails.status.toLowerCase() === 'premium' ? (
              'Upgrade to Ultimate'
            ) : (
              'Buy Subscription'
            )}
          </button>
        </div>
      </div>

      {/* Contact Section - Only show if user doesn't have active paid subscription */}
      {!hasActivePaidSubscription && (
        <div className="ds-card" style={{ maxWidth: '60rem', margin: '2rem auto 0 auto', textAlign: 'center' }}>
          <h3 className="ds-h4" style={{ margin: '0 0 1rem 0' }}>Need Help Choosing?</h3>
          <p className="ds-body ds-text-secondary" style={{ marginBottom: '1.5rem' }}>
            Contact our sales team to find the perfect plan for your business
          </p>
          <div className="ds-flex ds-justify-center ds-gap-4 ds-flex-wrap">
            <a href="https://wa.me/918814048362" target="_blank" rel="noopener noreferrer" className="ds-btn ds-btn-success" style={{ textDecoration: 'none' }}>
              <MessageSquare size={16} />
              WhatsApp
            </a>
            <a href="tel:+917015823248" className="ds-btn ds-btn-secondary" style={{ textDecoration: 'none' }}>
              <PhoneCall size={16} />
              Call Us
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
